import React, { useState } from 'react';
import { X, Building2, User, Briefcase, Check, ChevronRight, Info, Plus, Edit2, Star, Trash2, ChevronDown } from 'lucide-react';
interface Business {
  id: string;
  name: string;
  address: string;
  city: string;
  province: string;
  postalCode: string;
  hstNumber: string;
  registrationNumber: string;
  isDefault?: boolean;
}
interface FamilyMember {
  id: string;
  firstName: string;
  lastName: string;
  relationship: string;
  isDefault?: boolean;
}
interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  position: string;
  email: string;
  phone?: string;
  isDefault?: boolean;
}
interface PrefillSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onContinue: (selections: {
    business?: Business;
    familyMember?: FamilyMember;
    employee?: Employee;
  }) => void;
  businesses: Business[];
  familyMembers: FamilyMember[];
  employees: Employee[];
  documentType: string;
}
export function PrefillSelectionModal({
  isOpen,
  onClose,
  onContinue,
  businesses,
  familyMembers,
  employees,
  documentType
}: PrefillSelectionModalProps) {
  // Initialize with default selections
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(businesses.find(b => b.isDefault) || businesses[0] || null);
  const [selectedFamily, setSelectedFamily] = useState<FamilyMember | null>(familyMembers.find(f => f.isDefault) || familyMembers[0] || null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(employees.find(e => e.isDefault) || employees[0] || null);
  const handleContinue = () => {
    onContinue({
      business: selectedBusiness || undefined,
      familyMember: selectedFamily || undefined,
      employee: selectedEmployee || undefined
    });
  };
  if (!isOpen) return null;
  // Check if user has multiple options in any category
  const hasMultipleBusinesses = businesses.length > 1;
  const hasMultipleFamilyMembers = familyMembers.length > 1;
  const hasMultipleEmployees = employees.length > 1;
  const hasAnyMultiple = hasMultipleBusinesses || hasMultipleFamilyMembers || hasMultipleEmployees;
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-595">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" data-id="element-596">
        {/* Header */}
        <div className="p-6 border-b border-borderColor flex items-center justify-between sticky top-0 bg-white z-10" data-id="element-597">
          <div data-id="element-598">
            <h3 className="text-lg font-semibold text-primary" data-id="element-599">
              Select Document Prefill Settings
            </h3>
            <p className="text-sm text-secondary mt-1" data-id="element-600">
              Creating: {documentType}
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-aliceBlue rounded transition-colors" data-id="element-601">
            <X className="w-5 h-5 text-secondary" data-id="element-602" />
          </button>
        </div>

        {/* Info Banner */}
        <div className="p-4 mx-6 mt-6 bg-turquoise/5 border border-turquoise/20 rounded-lg" data-id="element-603">
          <div className="flex gap-3" data-id="element-604">
            <Info className="w-5 h-5 text-turquoise flex-shrink-0 mt-0.5" data-id="element-605" />
            <div data-id="element-606">
              <p className="text-sm font-semibold text-primary mb-1" data-id="element-607">
                Choose which information to prefill
              </p>
              <p className="text-xs text-secondary leading-relaxed" data-id="element-608">
                You have multiple saved profiles. Select which ones to use for
                this document.
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6" data-id="element-609">
          {/* Personal Information */}
          <div data-id="element-610">
            <div className="flex items-center justify-between mb-4" data-id="element-611">
              <h4 className="text-sm font-semibold text-primary" data-id="element-612">
                Personal Information
              </h4>
              <button onClick={() => {
              /* TODO: Open edit personal info modal */
            }} className="text-xs text-turquoise hover:underline flex items-center gap-1" data-id="element-613">
                <Edit2 className="w-3 h-3" data-id="element-614" />
                Edit
              </button>
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm" data-id="element-615">
              <div data-id="element-616">
                <p className="text-xs text-secondary mb-1" data-id="element-617">First Name</p>
                <p className="text-sm font-medium text-primary" data-id="element-618">James</p>
              </div>
              <div data-id="element-619">
                <p className="text-xs text-secondary mb-1" data-id="element-620">Last Name</p>
                <p className="text-sm font-medium text-primary" data-id="element-621">Wilson</p>
              </div>
              <div data-id="element-622">
                <p className="text-xs text-secondary mb-1" data-id="element-623">Email</p>
                <p className="text-sm font-medium text-primary" data-id="element-624">
                  james.wilson@example.com
                </p>
              </div>
              <div data-id="element-625">
                <p className="text-xs text-secondary mb-1" data-id="element-626">Phone Number</p>
                <p className="text-sm font-medium text-primary" data-id="element-627">
                  +1 (416) 555-0100
                </p>
              </div>
            </div>
          </div>

          {/* My Businesses */}
          {businesses.length > 0 && <div data-id="element-628">
              <div className="flex items-center justify-between mb-3" data-id="element-629">
                <h4 className="text-sm font-semibold text-primary" data-id="element-630">
                  My Businesses
                </h4>
                <button onClick={() => {
              /* TODO: Open add business modal */
            }} className="text-xs text-violetBlue hover:underline flex items-center gap-1" data-id="element-631">
                  <Plus className="w-3 h-3" data-id="element-632" />
                  Add Business
                </button>
              </div>
              <div className="space-y-2" data-id="element-633">
                {businesses.map(business => <button key={business.id} onClick={() => setSelectedBusiness(business)} className={`w-full flex items-center gap-3 py-2 px-3 rounded-lg transition-colors ${selectedBusiness?.id === business.id ? 'bg-turquoise/10 border-2 border-turquoise' : 'hover:bg-aliceBlue/50 border-2 border-transparent'}`} data-id="element-634">
                    <Building2 className="w-4 h-4 text-turquoise flex-shrink-0" data-id="element-635" />
                    <div className="flex-1 min-w-0 text-left" data-id="element-636">
                      <p className="text-sm font-medium text-primary truncate" data-id="element-637">
                        {business.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" data-id="element-638">
                      {selectedBusiness?.id === business.id && <Check className="w-5 h-5 text-turquoise" data-id="element-639" />}
                      {business.isDefault && <span className="px-2 py-0.5 bg-violetBlue/10 text-violetBlue rounded text-xs font-medium flex items-center gap-1" data-id="element-640">
                          <Star className="w-3 h-3 fill-violetBlue" data-id="element-641" />
                          Default
                        </span>}
                      <button onClick={e => {
                  e.stopPropagation();
                  /* TODO: Open edit business modal */
                }} className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded transition-colors" title="Edit" data-id="element-642">
                        <Edit2 className="w-4 h-4" data-id="element-643" />
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  /* TODO: Delete business */
                }} className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors" title="Delete" data-id="element-644">
                        <Trash2 className="w-4 h-4" data-id="element-645" />
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                }} className="p-1.5 text-secondary hover:bg-secondary/10 rounded transition-colors" data-id="element-646">
                        <ChevronDown className="w-4 h-4" data-id="element-647" />
                      </button>
                    </div>
                  </button>)}
              </div>
            </div>}

          {/* Family Members */}
          {familyMembers.length > 0 && <div data-id="element-648">
              <div className="flex items-center justify-between mb-3" data-id="element-649">
                <h4 className="text-sm font-semibold text-primary" data-id="element-650">
                  Family Members
                </h4>
                <button onClick={() => {
              /* TODO: Open add family modal */
            }} className="text-xs text-violetBlue hover:underline flex items-center gap-1" data-id="element-651">
                  <Plus className="w-3 h-3" data-id="element-652" />
                  Add Family Member
                </button>
              </div>
              <div className="space-y-2" data-id="element-653">
                {familyMembers.map(member => <button key={member.id} onClick={() => setSelectedFamily(member)} className={`w-full flex items-center gap-3 py-2 px-3 rounded-lg transition-colors ${selectedFamily?.id === member.id ? 'bg-turquoise/10 border-2 border-turquoise' : 'hover:bg-aliceBlue/50 border-2 border-transparent'}`} data-id="element-654">
                    <User className="w-4 h-4 text-violetBlue flex-shrink-0" data-id="element-655" />
                    <div className="flex-1 min-w-0 text-left" data-id="element-656">
                      <p className="text-sm font-medium text-primary truncate" data-id="element-657">
                        {member.firstName} {member.lastName}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" data-id="element-658">
                      {selectedFamily?.id === member.id && <Check className="w-5 h-5 text-turquoise" data-id="element-659" />}
                      {member.isDefault && <span className="px-2 py-0.5 bg-violetBlue/10 text-violetBlue rounded text-xs font-medium flex items-center gap-1" data-id="element-660">
                          <Star className="w-3 h-3 fill-violetBlue" data-id="element-661" />
                          Default
                        </span>}
                      <button onClick={e => {
                  e.stopPropagation();
                  /* TODO: Open edit family modal */
                }} className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded transition-colors" title="Edit" data-id="element-662">
                        <Edit2 className="w-4 h-4" data-id="element-663" />
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  /* TODO: Delete family member */
                }} className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors" title="Delete" data-id="element-664">
                        <Trash2 className="w-4 h-4" data-id="element-665" />
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                }} className="p-1.5 text-secondary hover:bg-secondary/10 rounded transition-colors" data-id="element-666">
                        <ChevronDown className="w-4 h-4" data-id="element-667" />
                      </button>
                    </div>
                  </button>)}
              </div>
            </div>}

          {/* Employees */}
          {employees.length > 0 && <div data-id="element-668">
              <div className="flex items-center justify-between mb-3" data-id="element-669">
                <h4 className="text-sm font-semibold text-primary" data-id="element-670">
                  Employees
                </h4>
                <button onClick={() => {
              /* TODO: Open add employee modal */
            }} className="text-xs text-violetBlue hover:underline flex items-center gap-1" data-id="element-671">
                  <Plus className="w-3 h-3" data-id="element-672" />
                  Add Employee
                </button>
              </div>
              <div className="space-y-2" data-id="element-673">
                {employees.map(employee => <button key={employee.id} onClick={() => setSelectedEmployee(employee)} className={`w-full flex items-center gap-3 py-2 px-3 rounded-lg transition-colors ${selectedEmployee?.id === employee.id ? 'bg-turquoise/10 border-2 border-turquoise' : 'hover:bg-aliceBlue/50 border-2 border-transparent'}`} data-id="element-674">
                    <Briefcase className="w-4 h-4 text-turquoise flex-shrink-0" data-id="element-675" />
                    <div className="flex-1 min-w-0 text-left" data-id="element-676">
                      <p className="text-sm font-medium text-primary truncate" data-id="element-677">
                        {employee.firstName} {employee.lastName}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0" data-id="element-678">
                      {selectedEmployee?.id === employee.id && <Check className="w-5 h-5 text-turquoise" data-id="element-679" />}
                      {employee.isDefault && <span className="px-2 py-0.5 bg-violetBlue/10 text-violetBlue rounded text-xs font-medium flex items-center gap-1" data-id="element-680">
                          <Star className="w-3 h-3 fill-violetBlue" data-id="element-681" />
                          Default
                        </span>}
                      <button onClick={e => {
                  e.stopPropagation();
                  /* TODO: Open edit employee modal */
                }} className="p-1.5 text-turquoise hover:bg-turquoise/10 rounded transition-colors" title="Edit" data-id="element-682">
                        <Edit2 className="w-4 h-4" data-id="element-683" />
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                  /* TODO: Delete employee */
                }} className="p-1.5 text-destructive hover:bg-destructive/10 rounded transition-colors" title="Delete" data-id="element-684">
                        <Trash2 className="w-4 h-4" data-id="element-685" />
                      </button>
                      <button onClick={e => {
                  e.stopPropagation();
                }} className="p-1.5 text-secondary hover:bg-secondary/10 rounded transition-colors" data-id="element-686">
                        <ChevronDown className="w-4 h-4" data-id="element-687" />
                      </button>
                    </div>
                  </button>)}
              </div>
            </div>}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-borderColor flex gap-3 sticky bottom-0 bg-white" data-id="element-688">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-borderColor text-primary rounded-lg hover:bg-aliceBlue transition-colors text-sm font-medium" data-id="element-689">
            Cancel
          </button>
          <button onClick={handleContinue} className="flex-1 px-4 py-2.5 bg-turquoise text-white rounded-lg hover:bg-turquoise/90 transition-colors text-sm font-medium flex items-center justify-center gap-2" data-id="element-690">
            Continue to Document
            <ChevronRight className="w-4 h-4" data-id="element-691" />
          </button>
        </div>
      </div>
    </div>;
}